import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "../App";
import { Star, PencilLine, ImagePlus, X, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

function Stars({ value = 0, size = "h-4 w-4", onChange = null }) {
  return (
    <div className="flex items-center gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onClick={() => onChange && onChange(n)}
          className={onChange ? "cursor-pointer" : "cursor-default"}
          data-testid={onChange ? `star-input-${n}` : undefined}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          <Star
            className={`${size} ${n <= value ? "fill-[#C5A059] text-[#C5A059]" : "text-[#D6CFC4]"}`}
          />
        </button>
      ))}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function ProductReviews({ productId }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [count, setCount] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [photos, setPhotos] = useState([]); // [{file_id, url}]
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/products/${productId}/reviews`)
      .then((r) => {
        setReviews(r.data.reviews || []);
        setAvg(r.data.average || 0);
        setCount(r.data.count || 0);
      })
      .catch(() => {
        // Silent fail — section just stays empty if reviews API errors out
        setReviews([]); setAvg(0); setCount(0);
      });
  }, [productId]);

  // Auto-open the form when the URL asks for it (used by review-reminder emails: /product/xxx?review=1)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("review") === "1") {
      setShowForm(true);
      // Scroll into view after paint
      setTimeout(() => {
        document.querySelector("[data-testid='product-reviews']")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [productId]);

  useEffect(() => {
    if (user) {
      if (!name) setName(user.name || "");
      if (!email) setEmail(user.email || "");
    }
  }, [user]);

  const pickPhotos = () => fileRef.current?.click();

  const onPhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-picking same file later
    if (!files.length) return;
    const room = 2 - photos.length;
    if (room <= 0) return toast.error("You can upload up to 2 photos");
    const chosen = files.slice(0, room);
    setUploading(true);
    try {
      const results = [];
      for (const f of chosen) {
        if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} is over 5 MB`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        const r = await axios.post(`${API}/reviews/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        results.push(r.data);
      }
      setPhotos((p) => [...p, ...results]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Please pick a star rating");
    if (!body.trim()) return toast.error("Please write your review");
    if (!user && (!name.trim() || !email.trim())) return toast.error("Please enter your name and email");
    setSubmitting(true);
    try {
      const payload = {
        product_id: productId,
        rating,
        title: title.trim() || null,
        body: body.trim(),
        images: photos.map((p) => p.url),
        reviewer_name: user ? undefined : name.trim(),
        reviewer_email: user ? undefined : email.trim(),
      };
      const r = await axios.post(`${API}/reviews`, payload);
      toast.success(r.data.message || "Thank you! Your review is under moderation.");
      setShowForm(false);
      setRating(0);
      setTitle("");
      setBody("");
      setPhotos([]);
      if (!user) { setName(""); setEmail(""); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit review. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-16 pt-10 border-t border-[#EAE3D9]" data-testid="product-reviews">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="label-small text-[#C5A059] mb-2">Customer Love</p>
          <h2 className="font-display text-3xl text-[#2C2A29]">Reviews & Ratings</h2>
          <div className="flex items-center gap-3 mt-3">
            <Stars value={Math.round(avg)} size="h-5 w-5" />
            <div className="text-sm text-[#595552]">
              {count > 0 ? (
                <>
                  <span className="font-semibold text-[#2C2A29]" data-testid="reviews-average">{avg.toFixed(1)}</span>
                  {" "}out of 5 · <span data-testid="reviews-count">{count} review{count > 1 ? "s" : ""}</span>
                </>
              ) : (
                <span className="text-[#8A8480]">Be the first to review this piece</span>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          variant="outline"
          className="border-[#C5A059] text-[#8B1E28] hover:bg-[#FFF5F3] rounded-full"
          data-testid="write-review-btn"
        >
          <PencilLine className="h-4 w-4 mr-2" /> {showForm ? "Close" : "Write a review"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="mt-6 p-6 bg-white border border-[#EAE3D9] rounded-2xl space-y-4" data-testid="review-form">
          <div>
            <div className="label-small text-[#8A8480] mb-2">Your rating</div>
            <Stars value={rating} size="h-7 w-7" onChange={setRating} />
          </div>
          {!user && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <div className="label-small text-[#8A8480] mb-1">Your name</div>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya S." data-testid="review-name-input" />
              </div>
              <div>
                <div className="label-small text-[#8A8480] mb-1">Your email (kept private)</div>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="review-email-input" />
              </div>
            </div>
          )}
          <div>
            <div className="label-small text-[#8A8480] mb-1">Title (optional)</div>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="A short headline for your review" data-testid="review-title-input" />
          </div>
          <div>
            <div className="label-small text-[#8A8480] mb-1">Your review</div>
            <Textarea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Tell us what you loved — craftsmanship, fit, finish, and how it made you feel."
              data-testid="review-body-input"
            />
          </div>
          <div>
            <div className="label-small text-[#8A8480] mb-2">Photos <span className="text-[#8A8480] normal-case">(optional, up to 2)</span></div>
            <div className="flex flex-wrap gap-3 items-start" data-testid="review-photos">
              {photos.map((ph, i) => (
                <div key={ph.file_id} className="relative h-20 w-20 rounded-xl overflow-hidden border border-[#EAE3D9]" data-testid={`review-photo-${i}`}>
                  <img src={ph.url} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center"
                    aria-label="Remove photo"
                    data-testid={`review-photo-remove-${i}`}>
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < 2 && (
                <button type="button" onClick={pickPhotos} disabled={uploading}
                  className="h-20 w-20 rounded-xl border-2 border-dashed border-[#EAE3D9] text-[#8A8480] hover:border-[#C5A059] hover:text-[#8B1E28] flex flex-col items-center justify-center gap-1 text-[10px] disabled:opacity-50"
                  data-testid="review-photo-add">
                  <ImagePlus className="h-5 w-5" />
                  {uploading ? "Uploading…" : "Add photo"}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={onPhotoChange} data-testid="review-photo-input" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setShowForm(false)} data-testid="review-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary rounded-full px-6" data-testid="review-submit-btn">
              {submitting ? "Submitting…" : "Submit review"}
            </Button>
          </div>
          <p className="text-xs text-[#8A8480]">Reviews go through a quick quality check before appearing here.</p>
        </form>
      )}

      {reviews.length > 0 && (
        <div className="mt-8 grid gap-4" data-testid="reviews-list">
          {reviews.map((r) => (
            <article key={r.review_id} className="p-5 bg-white border border-[#EAE3D9] rounded-2xl" data-testid={`review-${r.review_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[#FFF5F3] text-[#8B1E28] flex items-center justify-center font-semibold">
                    {(r.reviewer_name || "A").trim().charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#2C2A29] flex items-center gap-1.5">
                      {r.reviewer_name || "Anonymous"}
                      {r.is_verified_buyer && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                          title="This reviewer bought this exact piece from Sumanya"
                          data-testid={`review-${r.review_id}-verified`}
                        >
                          <ShieldCheck className="h-3 w-3" /> Verified purchase
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#8A8480]">{fmtDate(r.approved_at || r.created_at)}</div>
                  </div>
                </div>
                <Stars value={r.rating} />
              </div>
              {r.title && <h3 className="mt-3 font-semibold text-[#2C2A29]">{r.title}</h3>}
              <p className="mt-2 text-[#595552] leading-relaxed whitespace-pre-line">{r.body}</p>
              {(r.images || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`review-${r.review_id}-photos`}>
                  {r.images.map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noopener noreferrer"
                      className="block h-24 w-24 rounded-xl overflow-hidden border border-[#EAE3D9] hover:border-[#C5A059] transition-colors"
                      data-testid={`review-${r.review_id}-photo-${i}`}>
                      <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </a>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
